import { TFunction } from 'i18next'

import { ErrorMessages, GenericBlockerUpdate } from '@cash-web/shared-blocker-data-access-context'

export const getStatusError = <T extends GenericBlockerUpdate>(
  errors: ErrorMessages<T>,
  responseStatus: keyof ErrorMessages<T> | undefined,
  genericError: string
) => {
  return responseStatus != null ? errors[responseStatus] ?? genericError : genericError
}

export const getSelectOptionErrors = (t: TFunction) => {
  return {
    INVALID: t('The selected option is invalid.', { context: 'error message' }),
    CONCURRENT_MODIFICATION: t('There is an existing update in progress.', { context: 'error message' }),
  }
}

export const getSetAmountErrors = (t: TFunction) => {
  return {
    INVALID: t('The input is invalid.', { context: 'error message' }),
    FAILURE: t('Unable to submit your input', { context: 'error message' }),
  }
}

export const getSetSignatureErrors = (t: TFunction) => {
  return {
    INVALID: t('The signature is invalid.', { context: 'error message' }),
    FAILURE: t('Unable to submit your signature', { context: 'error message' }),
  }
}

export const getRegisterEmailErrors = (genericError: string, t: TFunction) => {
  return {
    INVALID: t('Please enter a valid email address.', { context: 'error message' }),
    INVALID_EMAIL_ADDRESS: t('Please enter a valid email address.', {
      context: 'error message',
    }),
    TOO_MANY_REQUESTS: t(
      'There have been too many attempts to register this email address. Please try again in 24 hours.',
      {
        context: 'error message',
      }
    ),
    DUPLICATE_EMAIL_ADDRESS: t('Please enter a valid email address.', { context: 'error message' }),
    INVALID_ENROLLMENT_TOKEN: t('Please enter a valid email address.', { context: 'error message' }),
    UNAUTHORIZED: genericError,
    CUSTOMER_DOES_NOT_EXIST: genericError,
  }
}

export const getLinkCardErrors = (t: TFunction) => {
  return {
    INVALID: t('This card is invalid', { context: 'error message' }),
    FAILURE: t('We were unable to link this card', {
      context: 'error message',
    }),
    INSTRUMENT_TYPE_MISMATCH: t('You attempted to link a credit card as a debit card, or vice versa', {
      context: 'error message',
    }),
  }
}

export const getRegisterSMSErrors = (genericError: string, t: TFunction) => {
  return {
    INVALID: t('We were unable to register that phone number.', { context: 'error message' }),
    INVALID_SMS_NUMBER: t('Please enter a valid phone number.', { context: 'error message' }),
    TOO_MANY_REQUESTS: t(
      'There have been too many attempts to register this phone number. Please try again in 24 hours.',
      {
        context: 'error message',
      }
    ),
    DUPLICATE_SMS_NUMBER: t('This phone number was already added.', {
      context: 'Error message for trying to add a phone number which already exists',
    }),
    UNAUTHORIZED: genericError,
    INVALID_ENROLLMENT_TOKEN: genericError,
    CUSTOMER_DOES_NOT_EXIST: genericError,
  }
}

export const getSetFullNameErrors = (genericError: string, t: TFunction) => {
  return {
    CONCURRENT_MODIFICATION: genericError,
    INVALID: t('Please enter a valid name.', { context: 'error message' }),
    INVALID_NAME: t('Please enter a valid name.', { context: 'error message' }),
  }
}

export const getVerifyEmailErrors = (t: TFunction, alias?: string) => {
  return {
    INVALID: t('Please enter a valid email address.', { context: 'error message' }),
    INVALID_EMAIL_ADDRESS: t('Please enter a valid email address.', { context: 'error message' }),
    INVALID_VERIFICATION_CODE: t("That doesn't look like the code we sent to {alias}", {
      context: 'error message',
      alias,
    }),
    EXPIRED_VERIFICATION_CODE: t('Code expired. Check for an email with a new code from Cash.', {
      context: 'error message',
    }),
    TOO_MANY_FAILED_ATTEMPTS: t(
      'There have been too many attempts to register this email address. Please try again in 24 hours.',
      {
        context: 'error message',
      }
    ),
    NOT_ELIGIBLE: t('This email address is already associated with an account and cannot be merged.', {
      context: 'error message',
    }),
  }
}

export const getSetCountryErrors = (t: TFunction) => {
  return {
    TOO_LATE_TO_CHANGE: t('Your account already has a country set.', { context: 'error message' }),
    COUNTRY_NOT_SUPPORTED: t('This country is not supported by Cash.', { context: 'error message' }),
    INVALID: t('This country is not supported by Cash.', { context: 'error message' }),
  }
}

export const getVerifyPasscodeErrors = (genericError: string, t: TFunction) => {
  return {
    INVALID: t('Invalid Cash PIN. Please try again.', { context: 'error message' }),
    INVALID_PASSCODE: t('Invalid Cash PIN. Please try again.', { context: 'error message' }),
    TOO_MANY_ATTEMPTS: t('There have been too many attempts to enter your Cash PIN. Please try again in 24 hours.', {
      context: 'error message',
    }),
    CARD_BLOCKED: t('There have been too many attempts to enter your Cash PIN. Please try again in 24 hours.', {
      context: 'error message',
    }),
    FAILURE: t('Unable to verify your Cash PIN. Link a new card to continue.', {
      context: 'error message',
    }),
    CONCURRENT_MODIFICATION: genericError,
  }
}

export const getVerifySMSErrors = (t: TFunction, alias?: string) => {
  return {
    INVALID: t('Please enter a valid phone number.', { context: 'error message' }),
    INVALID_SMS_NUMBER: t('Please enter a valid phone number.', { context: 'error message' }),
    INVALID_VERIFICATION_CODE: t("That doesn't look like the code we sent to {alias}.", {
      context: 'error message',
      alias,
    }),
    EXPIRED_VERIFICATION_CODE: t('Code expired. Check for a text with a new code from Cash.', {
      context: 'error message',
    }),
    TOO_MANY_FAILED_ATTEMPTS: t(
      'There have been too many attempts to register this phone number. Please try again in 24 hours.',
      {
        context: 'error message',
      }
    ),
    NOT_ELIGIBLE: t('This phone number is already associated with an account and cannot be merged.', {
      context: 'error message',
    }),
  }
}

export const getSetPaycheckAllocationAmountErrors = (genericError: string, t: TFunction) => {
  return {
    CONCURRENT_MODIFICATION: genericError,
    INVALID: t('Distribution amount is invalid', { context: 'error message' }),
  }
}
